export const HANDWRITING_STORAGE_PREFIX = "record-handwriting:v1:";
export const DOCUMENT_UNITS = 1000;
// Failed browser writes remain recoverable when a record modal closes and reopens.
const unsavedDocuments = new Map();
const saveErrorMessage = "手寫尚未儲存：瀏覽器儲存空間不足或無法使用。已暫存本次瀏覽，請勿重新整理。";

const finite = (value) => typeof value === "number" && Number.isFinite(value);
const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

export function validateAnnotations(value) {
  if (!Array.isArray(value)) return [];
  return value.slice(0, 10000).filter((item) => item && typeof item.id === "string" && /^#[0-9a-f]{6}$/i.test(item.color)
    && finite(item.width) && item.width > 0 && item.width <= 200
    && (item.type === "text" ? finite(item.x) && finite(item.y) && typeof item.text === "string" && finite(item.fontSize) && item.fontSize > 0 && item.fontSize <= 200
      : item.type === "stroke" && Array.isArray(item.points) && item.points.length > 0 && item.points.length <= 100000
      && item.points.every((point) => finite(point.x) && finite(point.y) && finite(point.p))))
    .map((item) => {
      if (item.type === "text") return { id: item.id, type: "text", color: item.color, width: item.width, x: item.x, y: item.y, fontSize: item.fontSize, text: item.text.slice(0, 20000) };
      const brush = item.brush === true && item.tool === "pen";
      return { id: item.id, type: "stroke", color: item.color, width: item.width, tool: ["pen", "pencil", "highlighter"].includes(item.tool) ? item.tool : "pen",
        ...(brush ? { brush: true } : {}), ...(item.smooth === true ? { smooth: true } : {}), points: item.points.map(({ x, y, p }) => ({ x, y, p: clamp(p, brush || item.smooth === true ? 0 : 0.1, 1) })) };
    });
}

export function loadAnnotations(storageKey) {
  if (unsavedDocuments.has(storageKey)) {
    const entries = unsavedDocuments.get(storageKey);
    return { entries, error: saveErrorMessage, readError: false, needsSave: true };
  }
  try {
    const raw = window.localStorage.getItem(`${HANDWRITING_STORAGE_PREFIX}${storageKey}`);
    if (!raw) return { entries: [], error: "" };
    const saved = JSON.parse(raw);
    if (saved.version !== 1 || !Array.isArray(saved.entries)) throw new Error("invalid handwriting data");
    const entries = validateAnnotations(saved.entries);
    if (entries.length !== saved.entries.length) throw new Error("invalid handwriting annotations");
    return { entries, error: "" };
  } catch (error) {
    return { entries: [], error: "無法讀取此瀏覽器的手寫紀錄，請先保留此視窗。", readError: true };
  }
}

export function saveAnnotations(storageKey, entries) {
  unsavedDocuments.set(storageKey, entries);
  try {
    window.localStorage.setItem(`${HANDWRITING_STORAGE_PREFIX}${storageKey}`, JSON.stringify({ version: 1, entries }));
    unsavedDocuments.delete(storageKey);
    return "";
  } catch (error) {
    return saveErrorMessage;
  }
}

const rounded = (value) => Math.round(value * 100) / 100;
const xy = ({ x, y }) => `${rounded(x)} ${rounded(y)}`;
const isBrush = (stroke) => stroke.tool === "pen" && stroke.brush === true;

function dotOutline(point, radius) {
  return `M ${rounded(point.x - radius)} ${rounded(point.y)} a ${rounded(radius)} ${rounded(radius)} 0 1 0 ${rounded(2 * radius)} 0 a ${rounded(radius)} ${rounded(radius)} 0 1 0 ${rounded(-2 * radius)} 0`;
}

// Rendering may omit redundant dense samples, but the saved input remains intact.
// Bound each fitting window so a long stroke cannot bridge distant bends, and
// retain pressure extrema with their neighbours before fitting a nearly straight run.
function simplifyOutlinePoints(stroke, radius) {
  const points = stroke.points;
  if (stroke.smooth !== true || points.length < 4) return points;
  const tolerance = Math.max(0.005, Math.min(0.2, stroke.width * 0.035));
  const pressureTolerance = Math.max(0.002, Math.min(0.025, stroke.width * 0.005));
  const maximumLength = Math.max(8, stroke.width * 6);
  const anchors = new Set([0, points.length - 1]);
  let travel = 0; let windowStart = 0;
  for (let index = 1; index < points.length; index++) {
    const before = points[index - 1]; const point = points[index];
    travel += Math.hypot(point.x - before.x, point.y - before.y);
    if (travel >= maximumLength || index - windowStart >= 128) {
      anchors.add(index); windowStart = index; travel = 0;
    }
    if (index === points.length - 1) continue;
    const after = points[index + 1];
    const ax = point.x - before.x; const ay = point.y - before.y;
    const bx = after.x - point.x; const by = after.y - point.y;
    const aLength = Math.hypot(ax, ay); const bLength = Math.hypot(bx, by);
    const pressureIn = point.p - before.p; const pressureOut = after.p - point.p;
    const pressureTurn = (pressureIn || pressureOut) && Math.sign(pressureIn) !== Math.sign(pressureOut);
    const corner = aLength > 0.00001 && bLength > 0.00001 && (ax * bx + ay * by) / (aLength * bLength) < 0.85;
    const stationary = aLength <= 0.00001 || bLength <= 0.00001;
    if (pressureTurn || corner || stationary) {
      anchors.add(index - 1); anchors.add(index); anchors.add(index + 1);
    }
  }
  const boundaries = [...anchors].sort((a, b) => a - b);
  const retained = new Set(boundaries);
  for (let boundary = 1; boundary < boundaries.length; boundary++) {
    const pending = [[boundaries[boundary - 1], boundaries[boundary]]];
    while (pending.length) {
      const [start, end] = pending.pop();
      if (end - start < 2) continue;
      const a = points[start]; const b = points[end];
      const dx = b.x - a.x; const dy = b.y - a.y; const lengthSquared = dx * dx + dy * dy;
      let largestError = 1; let split = -1;
      for (let index = start + 1; index < end; index++) {
        const point = points[index];
        const ratio = lengthSquared ? clamp(((point.x - a.x) * dx + (point.y - a.y) * dy) / lengthSquared, 0, 1) : 0;
        const positionError = Math.hypot(point.x - a.x - ratio * dx, point.y - a.y - ratio * dy) / tolerance;
        const pressureError = Math.abs(radius(point.p) - (radius(a.p) + (radius(b.p) - radius(a.p)) * ratio)) / pressureTolerance;
        const error = Math.max(positionError, pressureError);
        if (error > largestError) { largestError = error; split = index; }
      }
      if (split >= 0) {
        retained.add(split); pending.push([start, split], [split, end]);
      }
    }
  }
  return [...retained].sort((a, b) => a - b).map((index) => points[index]);
}

function brushOutline(stroke) {
  // The floor makes a released tip finite without turning zero into mouse pressure.
  const radius = (pressure) => Math.max(0.01, stroke.width / 2 * (0.06 + 0.94 * clamp(pressure, 0, 1) ** 1.2));
  const samples = simplifyOutlinePoints(stroke, radius);
  if (samples.length === 1) return dotOutline(samples[0], radius(samples[0].p));
  // A tap can include several stationary samples followed by a zero-pressure release.
  if (!samples.some((point) => Math.hypot(point.x - samples[0].x, point.y - samples[0].y) > 0.00001)) {
    return dotOutline(samples[0], radius(samples.reduce((maximum, point) => Math.max(maximum, point.p), 0)));
  }
  let points = samples;
  let terminalAnchorStart = -1;
  const lastSample = samples[samples.length - 1];
  if (lastSample.p === 0) {
    let tip = samples.length - 1;
    while (tip > 0 && Math.hypot(samples[tip - 1].x - lastSample.x, samples[tip - 1].y - lastSample.y) <= 0.00001) tip--;
    if (tip < samples.length - 1) {
      const contact = samples[tip]; const before = samples[tip - 1];
      const distance = Math.hypot(contact.x - before.x, contact.y - before.y);
      const taper = Math.min(distance / 4, stroke.width * 2);
      const anchor = (back) => ({ x: contact.x + (before.x - contact.x) * back / distance,
        y: contact.y + (before.y - contact.y) * back / distance, p: contact.p });
      // Two nearby anchors retain the measured contact peak before a short released tip.
      // Positions interpolate the last segment; pressure comes solely from that contact.
      points = [...samples.slice(0, tip), anchor(taper), anchor(taper / 2), lastSample];
      terminalAnchorStart = points.length - 3;
    }
  }

  const radii = points.map((point, index) => {
    const isTerminalContact = terminalAnchorStart >= 0 && index >= terminalAnchorStart && index < terminalAnchorStart + 2;
    const pressure = index === 0 || index === points.length - 1 || isTerminalContact ? point.p
      : (points[index - 1].p + 6 * point.p + points[index + 1].p) / 8;
    return radius(pressure);
  });
  return solidOutline(points, radii);
}

function solidOutline(points, radii) {
  // Fill a union of consistently wound segments and round joins in ONE path.
  // A single left/right ribbon folds over itself at Pencil jitter and reversals;
  // opposite winding then cuts triangular holes out of otherwise solid ink.
  const outline = [];
  points.forEach((point, index) => {
    const r = radii[index];
    outline.push(`${dotOutline(point, r)} Z`);
    if (!index) return;
    const before = points[index - 1];
    const dx = point.x - before.x; const dy = point.y - before.y;
    const length = Math.hypot(dx, dy);
    if (length <= 0.00001) return;
    const nx = -dy / length; const ny = dx / length;
    const previousRadius = radii[index - 1];
    outline.push(`M ${xy({ x: before.x + nx * previousRadius, y: before.y + ny * previousRadius })} L ${xy({ x: point.x + nx * r, y: point.y + ny * r })} L ${xy({ x: point.x - nx * r, y: point.y - ny * r })} L ${xy({ x: before.x - nx * previousRadius, y: before.y - ny * previousRadius })} Z`);
  });
  return outline.join(" ");
}

// One filled outline avoids dark seams in translucent highlighter strokes.
export function strokeOutline(stroke) {
  if (!stroke.points.length) return "";
  if (isBrush(stroke)) return brushOutline(stroke);
  const radius = (point) => stroke.width * (stroke.tool === "highlighter" ? 1 : 0.35 + 0.65 * point.p) / 2;
  const points = simplifyOutlinePoints(stroke, (pressure) => radius({ p: pressure }));
  // New Pencil/highlighter strokes use the same solid joins as pressure brushes.
  // Return one compound path so translucent ink is painted only once at overlaps.
  if (stroke.smooth === true) return solidOutline(points, points.map(radius));
  if (points.length === 1) {
    const point = points[0]; const r = radius(point);
    return `M ${rounded(point.x - r)} ${rounded(point.y)} a ${rounded(r)} ${rounded(r)} 0 1 0 ${rounded(2 * r)} 0 a ${rounded(r)} ${rounded(r)} 0 1 0 ${rounded(-2 * r)} 0`;
  }
  const left = []; const right = [];
  points.forEach((point, index) => {
    const before = points[Math.max(0, index - 1)]; const after = points[Math.min(points.length - 1, index + 1)];
    const dx = after.x - before.x; const dy = after.y - before.y; const length = Math.hypot(dx, dy) || 1;
    const r = radius(point); const nx = -dy / length * r; const ny = dx / length * r;
    left.push({ x: point.x + nx, y: point.y + ny }); right.push({ x: point.x - nx, y: point.y - ny });
  });
  const last = points[points.length - 1]; const first = points[0];
  const endRadius = radius(last); const startRadius = radius(first);
  return `M ${xy(left[0])} ${left.slice(1).map((point) => `L ${xy(point)}`).join(" ")} A ${rounded(endRadius)} ${rounded(endRadius)} 0 0 0 ${xy(right[right.length - 1])} ${right.slice(0, -1).reverse().map((point) => `L ${xy(point)}`).join(" ")} A ${rounded(startRadius)} ${rounded(startRadius)} 0 0 0 ${xy(left[0])} Z`;
}

export function annotationBounds(item) {
  if (item.type === "text") {
    const lines = item.text.split("\n");
    return { x: item.x, y: item.y, width: Math.max(...lines.map((line) => Array.from(line).reduce((sum, character) => sum + (character.charCodeAt(0) > 255 ? 1 : 0.62), 0)), 1) * item.fontSize,
      height: lines.length * item.fontSize * 1.3 };
  }
  const extents = item.points.reduce((box, point) => ({ left: Math.min(box.left, point.x), top: Math.min(box.top, point.y), right: Math.max(box.right, point.x), bottom: Math.max(box.bottom, point.y) }), { left: Infinity, top: Infinity, right: -Infinity, bottom: -Infinity });
  // Brush curves remain inside their control hull; account for path rounding too.
  const pad = isBrush(item) ? Math.max(item.width / 2, 0.01) + 0.02 : item.width / 2;
  return { x: extents.left - pad, y: extents.top - pad, width: extents.right - extents.left + 2 * pad, height: extents.bottom - extents.top + 2 * pad };
}

export function selectionBounds(entries, ids) {
  const bounds = entries.filter((item) => ids.includes(item.id)).map(annotationBounds);
  if (!bounds.length) return null;
  const x = Math.min(...bounds.map((box) => box.x)); const y = Math.min(...bounds.map((box) => box.y));
  return { x: x - 5, y: y - 5, width: Math.max(...bounds.map((box) => box.x + box.width)) - x + 10, height: Math.max(...bounds.map((box) => box.y + box.height)) - y + 10 };
}

export const pointInBounds = (point, box) => box && point.x >= box.x && point.x <= box.x + box.width && point.y >= box.y && point.y <= box.y + box.height;

export function pointInPolygon(point, polygon) {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[i]; const b = polygon[j];
    if ((a.y > point.y) !== (b.y > point.y) && point.x < (b.x - a.x) * (point.y - a.y) / (b.y - a.y) + a.x) inside = !inside;
  }
  return inside;
}

export function selectWithLasso(entries, polygon) {
  if (polygon.length < 3) return [];
  return entries.filter((item) => {
    const box = annotationBounds(item);
    return pointInPolygon({ x: box.x + box.width / 2, y: box.y + box.height / 2 }, polygon)
      || (item.type === "stroke" && item.points.some((point) => pointInPolygon(point, polygon)));
  }).map((item) => item.id);
}

export function translateAnnotations(entries, ids, dx, dy) {
  return entries.map((item) => !ids.includes(item.id) ? item : item.type === "text" ? { ...item, x: item.x + dx, y: item.y + dy }
    : { ...item, points: item.points.map((point) => ({ ...point, x: point.x + dx, y: point.y + dy })) });
}

export function distanceToSegment(point, start, end) {
  const dx = end.x - start.x; const dy = end.y - start.y; const length = dx * dx + dy * dy;
  const ratio = length ? clamp(((point.x - start.x) * dx + (point.y - start.y) * dy) / length, 0, 1) : 0;
  return Math.hypot(point.x - start.x - ratio * dx, point.y - start.y - ratio * dy);
}

function segmentsIntersect(a, b, c, d) {
  const cross = (p, q, r) => (q.x - p.x) * (r.y - p.y) - (q.y - p.y) * (r.x - p.x);
  const ac = cross(a, b, c); const ad = cross(a, b, d); const ca = cross(c, d, a); const cb = cross(c, d, b);
  return ac * ad < 0 && ca * cb < 0;
}

export function eraseAlongSegment(entries, start, end, radius) {
  return entries.filter((item) => {
    if (item.type === "text") {
      const box = annotationBounds(item); const padded = { x: box.x - radius, y: box.y - radius, width: box.width + 2 * radius, height: box.height + 2 * radius };
      if (pointInBounds(start, padded) || pointInBounds(end, padded)) return false;
      const corners = [{ x: padded.x, y: padded.y }, { x: padded.x + padded.width, y: padded.y }, { x: padded.x + padded.width, y: padded.y + padded.height }, { x: padded.x, y: padded.y + padded.height }];
      return !corners.some((point, index) => segmentsIntersect(start, end, point, corners[(index + 1) % 4]));
    }
    const tolerance = radius + item.width / 2;
    return !item.points.some((point, index) => {
      const previous = item.points[Math.max(0, index - 1)];
      return distanceToSegment(point, start, end) <= tolerance || distanceToSegment(start, previous, point) <= tolerance
        || distanceToSegment(end, previous, point) <= tolerance || segmentsIntersect(start, end, previous, point);
    });
  });
}

// Conservative geometric fitting; uncertain strokes remain freehand.
export function recognizeInkShape(input) {
  // Bound fitting work for long, coalesced Apple Pencil strokes.
  const samples = input.length > 512 ? Array.from({ length: 512 }, (_, i) => input[Math.round(i * (input.length - 1) / 511)]) : input;
  const points = [];
  // Compare with the last RETAINED point. Comparing adjacent raw samples drops
  // every sample of slow, densely sampled Pencil strokes, even a complete circle.
  samples.forEach((point, index) => {
    const previous = points[points.length - 1];
    if (!previous || index === samples.length - 1 || Math.hypot(point.x - previous.x, point.y - previous.y) > 0.5) points.push(point);
  });
  if (points.length < 2) return null;
  const first = points[0]; const last = points[points.length - 1];
  const length = points.slice(1).reduce((sum, point, index) => sum + Math.hypot(point.x - points[index].x, point.y - points[index].y), 0);
  const span = Math.hypot(last.x - first.x, last.y - first.y);
  const pressure = points.reduce((sum, point) => sum + point.p, 0) / points.length;
  const point = (x, y) => ({ x, y, p: pressure });
  if (span > 35 && length / span < 1.15 && Math.max(...points.map((p) => distanceToSegment(p, first, last))) < Math.max(3, span * 0.045)) {
    return { label: "直線", points: Array.from({ length: 33 }, (_, i) => point(first.x + (last.x - first.x) * i / 32, first.y + (last.y - first.y) * i / 32)) };
  }
  if (points.length < 5) return null;
  const mx = points.reduce((sum, p) => sum + p.x, 0) / points.length;
  const my = points.reduce((sum, p) => sum + p.y, 0) / points.length;
  const xx = points.reduce((sum, p) => sum + (p.x - mx) ** 2, 0);
  const yy = points.reduce((sum, p) => sum + (p.y - my) ** 2, 0);
  const xy = points.reduce((sum, p) => sum + (p.x - mx) * (p.y - my), 0);
  const angle = Math.atan2(2 * xy, xx - yy) / 2;
  const c = Math.cos(angle); const s = Math.sin(angle);
  const local = points.map((p) => ({ x: (p.x - mx) * c + (p.y - my) * s, y: -(p.x - mx) * s + (p.y - my) * c }));
  const world = (x, y) => point(mx + x * c - y * s, my + x * s + y * c);
  const xmin = Math.min(...local.map((p) => p.x)); const xmax = Math.max(...local.map((p) => p.x));
  const ymin = Math.min(...local.map((p) => p.y)); const ymax = Math.max(...local.map((p) => p.y));
  const rx = (xmax - xmin) / 2; const ry = (ymax - ymin) / 2;
  const cx = (xmax + xmin) / 2; const cy = (ymax + ymin) / 2;
  if (Math.min(rx, ry) > 12 && span < Math.max(rx, ry) * 0.45) {
    const radii = local.map((p) => Math.hypot((p.x - cx) / rx, (p.y - cy) / ry));
    const error = Math.sqrt(radii.reduce((sum, r) => sum + (r - 1) ** 2, 0) / radii.length);
    const angles = local.map((p) => Math.atan2((p.y - cy) / ry, (p.x - cx) / rx));
    const turns = angles.slice(1).map((a, i) => Math.atan2(Math.sin(a - angles[i]), Math.cos(a - angles[i])));
    const winding = turns.reduce((sum, a) => sum + a, 0);
    const travel = turns.reduce((sum, a) => sum + Math.abs(a), 0);
    if (error < 0.14 && Math.abs(winding) > 5.4 && Math.abs(winding) < 7 && travel < 7.8 && length > 4 * Math.max(rx, ry)) {
      const circle = Math.abs(rx - ry) / Math.max(rx, ry) < 0.20;
      const a = circle ? (rx + ry) / 2 : rx; const b = circle ? a : ry;
      const start = angles[0]; const direction = Math.sign(winding);
      return { label: circle ? "圓形" : "橢圓", points: Array.from({ length: 97 }, (_, i) => world(cx + a * Math.cos(start + direction * i / 96 * 2 * Math.PI), cy + b * Math.sin(start + direction * i / 96 * 2 * Math.PI))) };
    }
  }
  // Fit y = a*x² + b*x + c in either principal direction, using normalized coordinates.
  for (const swap of [false, true]) {
    const data = local.map((p) => swap ? { x: p.y, y: p.x } : p);
    const min = Math.min(...data.map((p) => p.x)); const max = Math.max(...data.map((p) => p.x));
    const scale = (max - min) / 2; const mid = (max + min) / 2;
    if (scale < 25) continue;
    const normalized = data.map((p) => ({ x: (p.x - mid) / scale, y: p.y }));
    const matrix = [0, 1, 2].map((row) => [0, 1, 2].map((col) => normalized.reduce((sum, p) => sum + p.x ** (row + col), 0)).concat(normalized.reduce((sum, p) => sum + p.y * p.x ** row, 0)));
    let valid = true;
    for (let col = 0; col < 3; col++) {
      const pivot = [col, ...Array.from({ length: 2 - col }, (_, i) => col + 1 + i)].sort((a, b) => Math.abs(matrix[b][col]) - Math.abs(matrix[a][col]))[0];
      [matrix[col], matrix[pivot]] = [matrix[pivot], matrix[col]];
      const divisor = matrix[col][col];
      if (Math.abs(divisor) < 1e-8) { valid = false; break; }
      matrix[col] = matrix[col].map((value) => value / divisor);
      for (let row = 0; row < 3; row++) if (row !== col) {
        const factor = matrix[row][col]; matrix[row] = matrix[row].map((value, i) => value - factor * matrix[col][i]);
      }
    }
    if (!valid) continue;
    const [constant, linear, quadratic] = matrix.map((row) => row[3]);
    const fitted = (x) => constant + linear * x + quadratic * x * x;
    const error = Math.sqrt(normalized.reduce((sum, p) => sum + (p.y - fitted(p.x)) ** 2, 0) / data.length);
    const travel = data.slice(1).reduce((sum, p, i) => sum + Math.abs(p.x - data[i].x), 0);
    const vertex = -linear / (2 * quadratic);
    if (Math.abs(quadratic) < scale * 0.15 || Math.abs(vertex) > 0.8 || error > Math.max(2, scale * 0.045) || travel > scale * 2.3 || Math.abs(data[0].x - data[data.length - 1].x) < scale * 1.8) continue;
    const start = normalized[0].x; const end = normalized[normalized.length - 1].x;
    return { label: "拋物線", points: Array.from({ length: 65 }, (_, i) => {
      const x = start + (end - start) * i / 64; const u = mid + x * scale; const v = fitted(x);
      return swap ? world(v, u) : world(u, v);
    }) };
  }
  return null;
}
